import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {from, Observable, of, shareReplay, map, switchMap, catchError} from 'rxjs';
import {environment} from '../../environments/environment';

export interface AppConfig {
  attributesEnabled: boolean;
  moderationRequired: boolean;
  placesEnabled?: boolean;
  placesDefaultCountry?: string;
}

const DEFAULT_APP_CONFIG: AppConfig = {
  attributesEnabled: true,
  moderationRequired: true,
  placesEnabled: false,
  placesDefaultCountry: '',
};

@Injectable({providedIn: 'root'})
export class AppConfigService {
  private config$?: Observable<AppConfig>;

  constructor(private http: HttpClient) {}

  getConfig(): Observable<AppConfig> {
    if (!this.config$) {
      this.config$ = from(environment).pipe(
        switchMap((env: any) => this.http.get<AppConfig>(`${env.backendURL}/api/config`)),
        map(config => ({
          attributesEnabled: config?.attributesEnabled !== false,
          moderationRequired: config?.moderationRequired !== false,
          placesEnabled: !!config?.placesEnabled,
          placesDefaultCountry: (config?.placesDefaultCountry || '')
            .toUpperCase()
            .split(/[,;\s]+/)
            .map(p => p.trim().slice(0, 2))
            .filter(p => /^[A-Z]{2}$/.test(p))
            .join(',')
        })),
        catchError(() => of({...DEFAULT_APP_CONFIG})),
        shareReplay(1)
      );
    }
    return this.config$;
  }

  /** Drop cached config so Admin maps changes are visible without full reload. */
  refresh(): void {
    this.config$ = undefined;
  }

  attributesEnabled(): Observable<boolean> {
    return this.getConfig().pipe(map(config => config.attributesEnabled));
  }

  moderationRequired(): Observable<boolean> {
    return this.getConfig().pipe(map(config => config.moderationRequired));
  }
}
